/**
 * Форма внутреннего заказа — блок `WEB-3.5`, требование 322, решения `Б2`…`Б7`.
 *
 * Тело — ровно `InternalWorkInput`: маршрут, даты, груз, вес, ADR, кузов, комментарий.
 * Условий сделки у заказа нет, и полей под них здесь нет. Поля и переподпись — общие с
 * формой объявления (`cargo-form-parts.ts`). Пределы и право судит сервер: своя фирма
 * без права видит форму, а отказ `403` — над ней, с введённым на месте (`Б2`, `Б6`).
 *
 * Ключ идемпотентности — как у объявления: повтор при обрыве тот же, новый после
 * ответа сервера или правки поля, второй запрос в пути не уходит (`Б5`). Успех ведёт
 * на карточку работы: её читает сервер (`Б7`).
 */

import { ApiFailure, UNAUTHENTICATED_CODE, newIdempotencyKey } from '../api/client';
import type { ApiClient, InternalWorkSubmission } from '../api/client';
import type { Work } from '../api/contract';
import type { Strings } from '../i18n';
import { navigate } from '../router';
import { createCargoFormParts, integerOf, showFormRefusal, textOf } from './cargo-form-parts';
import { placeState, renderFieldMessages, screenStateFor } from './state';
import type { ScreenState } from './state';

/**
 * Статус действия, которым сервер предлагает отменить внутренний заказ (`Б11`). Живёт
 * здесь, а не в карточке: там он встал бы рядом с другим статусом (сторожа `F4`, `WEB12_19`).
 */
export const INTERNAL_CANCEL_ACTION: Work['status'] = 'CANCELLED';

export interface InternalWorkFormScreenOptions {
  readonly client: ApiClient;
  readonly strings: Strings;
  readonly companyId: string;
  readonly onUnauthenticated: () => void;
}

export interface InternalWorkFormScreen {
  readonly element: HTMLElement;
  /** Состояние экрана либо `null` — форма показана. */
  readonly state: ScreenState | null;
  load(): Promise<void>;
  /** Подписи на новом языке; введённое остаётся на месте (долг `TD-72`). */
  relabel(next: Strings): void;
}

export function createInternalWorkFormScreen(options: InternalWorkFormScreenOptions): InternalWorkFormScreen {
  const { client, companyId, onUnauthenticated } = options;
  let strings = options.strings;

  const element = document.createElement('section');
  const heading = document.createElement('h1');
  const notice = document.createElement('p');
  const form = document.createElement('form');
  const submitButton = document.createElement('button');
  const parts = createCargoFormParts({
    client,
    strings,
    weightCaption: (shown) => shown.internal_work_form_weight,
  });

  element.className = 'screen screen-internal-work-form';
  notice.className = 'notice';
  notice.setAttribute('role', 'alert');
  form.className = 'form';
  form.noValidate = true;
  submitButton.type = 'submit';
  submitButton.className = 'action action--primary';

  parts.caption(() => {
    heading.textContent = strings.internal_work_form_title;
    submitButton.textContent = strings.internal_work_form_submit;
  });

  form.append(
    parts.origin.label,
    parts.destination.label,
    parts.loadingAtField,
    parts.unloadByField,
    parts.cargoTypeField,
    parts.weightField,
    parts.adrField,
    parts.bodyTypeField,
    parts.bodyArea,
    parts.commentField,
    submitButton,
  );

  let state: ScreenState | null = 'loading';
  /** Пришёл ли справочник кузовов: без него формы нет. */
  let answered = false;
  /** Запрос в пути: повтор отправки до ответа не уходит вторым запросом. */
  let sending = false;
  /** Ключ идемпотентности текущей отправки либо `null`. */
  let idempotencyKey: string | null = null;

  function render(): void {
    placeState(element, { state, answered, frame: [heading, notice], list: form }, strings);
  }

  /** Тело — ровно поля формы контракта; пустое поле уходит `null`. */
  function entered(): InternalWorkSubmission {
    return {
      origin_city_id: integerOf(parts.origin.select.value),
      destination_city_id: integerOf(parts.destination.select.value),
      loading_at: textOf(parts.loadingAt.value),
      unload_by: textOf(parts.unloadBy.value),
      cargo_type: textOf(parts.cargoType.value),
      weight_kg: integerOf(parts.weight.value),
      adr: parts.adr.checked,
      body: parts.enteredBody(),
      comment: textOf(parts.comment.value),
    };
  }

  function signedOut(error: unknown): boolean {
    return error instanceof ApiFailure && error.code === UNAUTHENTICATED_CODE;
  }

  async function load(): Promise<void> {
    state = screenStateFor({ kind: 'pending' });
    answered = false;
    render();

    try {
      parts.setBodyTypes(await client.listBodyTypes());
      answered = true;
      state = screenStateFor({ kind: 'done', empty: false });
    } catch (error) {
      if (signedOut(error)) {
        onUnauthenticated();

        return;
      }

      state = screenStateFor({ kind: 'failed', code: error instanceof ApiFailure ? error.code : null });
    }

    render();
  }

  async function send(): Promise<void> {
    sending = true;
    submitButton.disabled = true;
    notice.textContent = '';
    renderFieldMessages(form, []);

    try {
      idempotencyKey ??= newIdempotencyKey();

      const saved = await client.createInternalWork(companyId, entered(), idempotencyKey);

      idempotencyKey = null;
      navigate({ kind: 'workCard', companyId, workId: saved.id });
    } catch (error) {
      if (signedOut(error)) {
        onUnauthenticated();

        return;
      }

      if (error instanceof ApiFailure && error.code !== null) {
        // Сервер ответил — следующая отправка новая и несёт новый ключ. Обрыв
        // ответа не дал: заказ, возможно, заведён, и ключ остаётся.
        idempotencyKey = null;
      }

      showFormRefusal(form, notice, error, strings);
    } finally {
      sending = false;
      submitButton.disabled = false;
    }
  }

  // Правка любого поля — новая отправка: ключ прежней снимается.
  form.addEventListener('input', () => {
    idempotencyKey = null;
  });
  form.addEventListener('change', () => {
    idempotencyKey = null;
  });

  form.addEventListener('submit', (event) => {
    event.preventDefault();

    if (sending) {
      return;
    }

    void send();
  });

  function relabel(next: Strings): void {
    strings = next;
    parts.relabel(next);
    render();
  }

  render();

  return {
    element,

    get state(): ScreenState | null {
      return state;
    },

    load,
    relabel,
  };
}
